/**
 * Axios Configuration with CSRF Protection
 *
 * This module configures axios with:
 * - CSRF token in headers for all state-changing requests (POST, PUT, DELETE, PATCH)
 * - Request/response interceptors for common handling
 */

import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { getCsrfToken, requiresCsrfProtection } from '../utils/csrf';
import { circuitBreakerManager, CircuitBreakerError } from '../utils/circuitBreaker';

// Set global axios defaults for all requests
// Required for CSRF - browser must send cookies with cross-origin requests
axios.defaults.withCredentials = true;

/**
 * Create an axios instance with CSRF protection
 */
export function createAxiosWithCsrf(): AxiosInstance {
  const instance = axios.create({
    // Enable sending cookies with cross-origin requests
    // Required for CSRF validation - Spring Security compares
    // X-XSRF-TOKEN header with XSRF-TOKEN cookie sent by browser
    withCredentials: true
  });

  // Add CSRF token to state-changing requests
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const method = config.method?.toUpperCase() || 'GET';

      if (requiresCsrfProtection(method)) {
        config.headers = config.headers || {};
        config.headers['X-XSRF-TOKEN'] = getCsrfToken();
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  return instance;
}

/**
 * Default axios instance with CSRF protection enabled
 */
const axiosWithCsrf = createAxiosWithCsrf();

export default axiosWithCsrf;

/**
 * Helper to add CSRF headers to manual fetch/axios calls
 * Use this when not using the default axiosWithCsrf instance
 */
export function withCsrfHeaders(headers: Record<string, string> = {}): Record<string, string> {
  return {
    ...headers,
    'X-XSRF-TOKEN': getCsrfToken()
  };
}

// Circuit Breaker Integration

/**
 * Execute an axios request with circuit breaker protection
 *
 * @param requestFn Function that returns an axios request
 * @param serviceName Service name for circuit breaker tracking
 * @param fallback Optional fallback function to execute when circuit is open
 * @returns The response data
 *
 * @example
 * const data = await executeWithCircuitBreaker(
 *   () => axiosWithCsrf.get('/api/orders'),
 *   'api-orders',
 *   () => ({ orders: [] }) // fallback
 * );
 */
export async function executeWithCircuitBreaker<T>(
  requestFn: () => Promise<any>,
  serviceName: string,
  fallback?: () => T | Promise<T>
): Promise<T> {
  return circuitBreakerManager.execute(
    serviceName,
    async () => {
      const response = await requestFn();
      return response.data || response;
    },
    fallback
  );
}

/**
 * Get the current state of all circuit breakers for monitoring
 */
export function getCircuitBreakerStates() {
  return circuitBreakerManager.getAllStates();
}

/**
 * Export circuit breaker manager and error for advanced usage
 */
export { circuitBreakerManager, CircuitBreakerError };
